import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(!token ? t('auth.reset.invalidTokenError') : null);

  // SEO: Prevent indexing of password reset page
  useEffect(() => {
    document.title = 'Reset Password | Ridalo';
    const metaRobots = document.querySelector('meta[name="robots"]') || document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    metaRobots.setAttribute('content', 'noindex, nofollow');
    if (!document.querySelector('meta[name="robots"]')) {
      document.head.appendChild(metaRobots);
    }
    return () => {
      if (metaRobots.parentNode) {
        metaRobots.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!token) setError(t('auth.reset.invalidTokenError'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t('auth.reset.errorMinLen'));
      return;
    }
    if (password !== confirm) {
      toast.error(t('auth.reset.errorMismatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail === 'Invalid or expired token'
        ? t('auth.reset.errorExpired')
        : (err.response?.data?.detail === 'Token expired'
            ? t('auth.reset.errorExpiredHour')
            : t('auth.reset.errorGeneric')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-ink-light hover:text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> {t('auth.reset.backToLogin')}
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">{t('auth.reset.doneTitle')}</h1>
              <p className="text-sm text-ink-light mb-4">{t('auth.reset.doneSubtitle')}</p>
            </div>
          ) : error ? (
            <div className="text-center" data-testid="reset-error">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-7 h-7 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">{t('auth.reset.invalidTitle')}</h1>
              <p className="text-sm text-ink-light mb-4">{error}</p>
              <Link to="/forgot-password" className="btn-primary inline-block">
                {t('auth.reset.restart')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-primary mb-2">{t('auth.reset.title')}</h1>
              <p className="text-sm text-ink-light mb-6">
                {t('auth.reset.subtitle')}
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-primary mb-2 block">{t('auth.reset.newPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-field pl-10"
                      required
                      autoFocus
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-primary mb-2 block">{t('auth.reset.confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      className="input-field pl-10"
                      required
                      minLength={6}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? t('auth.reset.saving') : t('auth.reset.submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
